import { useState, useEffect, type FormEvent } from 'react';
import VersionDisplay from '@/components/VersionDisplay';
import { audioManager } from '@/utils/audioManager';
import { authService } from '@/services/auth';
import { auth } from '@/services/firebase/config';
import { signInAnonymously } from 'firebase/auth';
import { INFO_SECTION_ORDER, INFO_SECTIONS } from '@/content/lobbyInfoSections';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';

const DEMO_GAME_CODE = '947107';

const Lobby = () => {
  const navigate = useSafeNavigate();
  const [gameCode, setGameCode] = useState('');
  const [showGameCodeInput, setShowGameCodeInput] = useState(false);
  const [aiGameCode, setAiGameCode] = useState('');
  const [showAiGameCodeInput, setShowAiGameCodeInput] = useState(false);
  const [aiGameError, setAiGameError] = useState<string | null>(null);
  const [isLoadingAiGame, setIsLoadingAiGame] = useState(false);

  // Initialize audio system on component mount
  useEffect(() => {
    const initializeAudio = async () => {
      if (!audioManager.isReady()) {
        try {
          await audioManager.initialize();
        } catch (error) {
          console.warn(
            '⚠️ Failed to initialize audio (may need user interaction):',
            error
          );
        }
      }
    };

    initializeAudio();
  }, []);

  const ensureAnonymousAuth = async () => {
    try {
      if (!auth.currentUser) {
        await signInAnonymously(auth);
      }
    } catch (error) {
      console.error(
        '❌ Anonymous authentication failed while joining AI game:',
        error
      );

      if (error instanceof Error) {
        if (error.message.includes('auth/admin-restricted-operation')) {
          throw new Error(
            'Anonymous authentication is disabled. Please contact the teacher to enable guest access.'
          );
        }

        if (error.message.includes('auth/network-request-failed')) {
          throw new Error(
            'Network connection failed. Please check your internet connection.'
          );
        }
      }

      throw new Error(
        'Authentication failed. Please try again or contact support.'
      );
    }
  };

  const initializeAudioOnInteraction = async () => {
    if (!audioManager.isReady()) {
      try {
        await audioManager.initialize();
      } catch (error) {
        console.warn('⚠️ Audio initialization failed:', error);
      }
    }
  };

  const handlePlayWithAI = async () => {
    await initializeAudioOnInteraction();

    // Show AI game code input popup
    setShowAiGameCodeInput(true);
    setAiGameError(null);
    setAiGameCode('');
  };

  const handleGameCodeSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (gameCode.trim()) {
      await initializeAudioOnInteraction();

      // Navigate to game with the entered code
      navigate(`/join-game/${gameCode.trim().toUpperCase()}`);
    }
  };

  const handleEnterGameCode = async () => {
    await initializeAudioOnInteraction();
    setShowGameCodeInput(true);
  };

  const handleDemoGame = async () => {
    await initializeAudioOnInteraction();
    navigate(`/join-game/${DEMO_GAME_CODE}`);
  };

  const handleAiGameCodeSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!aiGameCode.trim() || aiGameCode.trim().length !== 6) {
      setAiGameError('Please enter a valid 6-digit game code');
      return;
    }

    try {
      setIsLoadingAiGame(true);
      setAiGameError(null);

      await ensureAnonymousAuth();

      // Look up the AI game session
      const aiSession = await authService.getAiGameSession(
        aiGameCode.trim().toUpperCase(),
        true
      );

      if (!aiSession) {
        setAiGameError('Game not found. Please check the code and try again.');
        return;
      }

      if (aiSession.status === 'expired') {
        setAiGameError(
          'This game has expired. Please contact your teacher for a new code.'
        );
        return;
      }

      if (aiSession.status === 'cancelled') {
        setAiGameError(
          'This game has been cancelled. Please contact your teacher.'
        );
        return;
      }

      // Navigate to AI setup with the AI game session ID in the URL
      navigate(
        `/ai-setup?aiGameId=${aiSession.id}&gameCode=${aiGameCode.trim().toUpperCase()}`
      );
    } catch (error: unknown) {
      console.error('❌ Error looking up AI game:', error);
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to find game. Please try again.';
      setAiGameError(message);
    } finally {
      setIsLoadingAiGame(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        boxSizing: 'border-box',
        padding: '2rem 1.5rem 3rem',
        overflow: 'hidden',
      }}
    >
      {/* Teacher Dash Button - Top Left */}
      <button
        onClick={() => navigate('/teacher')}
        style={{
          position: 'absolute',
          top: '1.5rem',
          left: '1.5rem',
          padding: '0.8rem 1.5rem',
          fontSize: '1rem',
          backgroundColor: '#2196F3',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          fontWeight: 'bold',
          boxShadow: '0 4px 16px rgba(33,150,243,0.4)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          zIndex: 10,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 6px 20px rgba(33,150,243,0.6)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 16px rgba(33,150,243,0.4)';
        }}
      >
        👩‍🏫 Teacher Dash
      </button>

      <div
        style={{
          width: '100%',
          maxWidth: '1100px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1.25rem',
          paddingTop: '2rem',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <VersionDisplay variant="transparent" />

        <div
          style={{
            display: 'flex',
            gap: '0.75rem',
            justifyContent: 'center',
            flexWrap: 'wrap',
            width: '100%',
          }}
        >
          {INFO_SECTION_ORDER.map((sectionId) => {
            const section = INFO_SECTIONS[sectionId];

            return (
              <button
                key={sectionId}
                type="button"
                onClick={() => navigate(section.path)}
                style={{
                  padding: '0.78rem 1.35rem',
                  minWidth: '138px',
                  fontSize: '0.92rem',
                  background: 'rgba(24, 32, 95, 0.82)',
                  color: 'white',
                  border: '2px solid rgba(255,255,255,0.18)',
                  borderRadius: '24px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  letterSpacing: '0.01em',
                  boxShadow: '0 10px 24px rgba(10, 18, 70, 0.22)',
                  backdropFilter: 'blur(12px)',
                  transition:
                    'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.borderColor = `${section.accent}`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.18)';
                }}
              >
                {section.label}
              </button>
            );
          })}
        </div>

        <div
          style={{
            backgroundColor: 'rgba(255,255,255,0.1)',
            padding: '3rem',
            borderRadius: '20px',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            maxWidth: '680px',
            width: '100%',
            textAlign: 'center',
          }}
        >
          <h1
            style={{
              fontSize: '3.5rem',
              marginBottom: '1rem',
              background: 'linear-gradient(45deg, #FFD700, #FFA500)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: 'none',
            }}
          >
            🏃‍♂️ Home Run
          </h1>

          <div
            style={{
              backgroundColor: 'rgba(255,255,255,0.1)',
              padding: '1.5rem',
              borderRadius: '12px',
              marginBottom: '2rem',
            }}
          >
            <h2 style={{ marginBottom: '1rem', color: '#FFD700' }}>
              Get Ready For Some Fun!
            </h2>
            <div
              style={{ textAlign: 'left', maxWidth: '460px', margin: '0 auto' }}
            >
              <p>• Playing with your teacher? Enter the code.</p>
              <p>
                • Want solo practice? Join an AI game your teacher has shared.
              </p>
              <p>• Want a quick look? Launch the demo game below.</p>
            </div>
          </div>

          {/* Game Code Input Section */}
          {showGameCodeInput && (
            <div
              style={{
                backgroundColor: 'rgba(255,255,255,0.15)',
                padding: '2rem',
                borderRadius: '12px',
                marginBottom: '2rem',
                border: '2px solid rgba(255,215,0,0.3)',
              }}
            >
              <h3 style={{ marginBottom: '1rem', color: '#FFD700' }}>
                Enter Game Code
              </h3>
              <form
                onSubmit={handleGameCodeSubmit}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem',
                }}
              >
                <input
                  type="text"
                  value={gameCode}
                  onChange={(e) => setGameCode(e.target.value.toUpperCase())}
                  placeholder="Enter 6-digit game code"
                  maxLength={6}
                  style={{
                    padding: '1rem',
                    fontSize: '1.5rem',
                    textAlign: 'center',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    border: '2px solid rgba(255,215,0,0.5)',
                    borderRadius: '8px',
                    outline: 'none',
                    color: '#333',
                    fontWeight: 'bold',
                    letterSpacing: '0.2em',
                  }}
                  autoFocus
                />
                <div
                  style={{
                    display: 'flex',
                    gap: '1rem',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  <button
                    type="submit"
                    disabled={gameCode.length !== 6}
                    style={{
                      padding: '0.8rem 2rem',
                      fontSize: '1.1rem',
                      backgroundColor:
                        gameCode.length === 6
                          ? '#4CAF50'
                          : 'rgba(255,255,255,0.3)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: gameCode.length === 6 ? 'pointer' : 'not-allowed',
                      fontWeight: 'bold',
                      opacity: gameCode.length === 6 ? 1 : 0.5,
                    }}
                  >
                    🚀 Join Game
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowGameCodeInput(false)}
                    style={{
                      padding: '0.8rem 2rem',
                      fontSize: '1.1rem',
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* AI Game Code Input Section */}
          {showAiGameCodeInput && (
            <div
              style={{
                backgroundColor: 'rgba(255,255,255,0.15)',
                padding: '2rem',
                borderRadius: '12px',
                marginBottom: '2rem',
                border: '2px solid rgba(255,152,0,0.4)',
              }}
            >
              <h3 style={{ marginBottom: '0.5rem', color: '#FF9800' }}>
                🤖 Enter AI Game Code
              </h3>
              <p
                style={{
                  marginBottom: '1rem',
                  fontSize: '0.9rem',
                  opacity: 0.9,
                }}
              >
                Enter the code provided by your teacher
              </p>
              <form
                onSubmit={handleAiGameCodeSubmit}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem',
                }}
              >
                <input
                  type="text"
                  value={aiGameCode}
                  onChange={(e) => setAiGameCode(e.target.value.toUpperCase())}
                  placeholder="Enter 6-digit game code"
                  maxLength={6}
                  style={{
                    padding: '1rem',
                    fontSize: '1.5rem',
                    textAlign: 'center',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    border: '2px solid rgba(255,152,0,0.6)',
                    borderRadius: '8px',
                    outline: 'none',
                    color: '#333',
                    fontWeight: 'bold',
                    letterSpacing: '0.2em',
                  }}
                  autoFocus
                />
                {aiGameError && (
                  <div
                    style={{
                      padding: '0.75rem',
                      backgroundColor: 'rgba(244,67,54,0.2)',
                      border: '2px solid rgba(244,67,54,0.4)',
                      borderRadius: '8px',
                      color: '#FFB4B4',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                    }}
                  >
                    ⚠️ {aiGameError}
                  </div>
                )}
                <div
                  style={{
                    display: 'flex',
                    gap: '1rem',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  <button
                    type="submit"
                    disabled={aiGameCode.length !== 6 || isLoadingAiGame}
                    style={{
                      padding: '0.8rem 2rem',
                      fontSize: '1.1rem',
                      backgroundColor:
                        aiGameCode.length === 6 && !isLoadingAiGame
                          ? '#FF9800'
                          : 'rgba(255,255,255,0.3)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor:
                        aiGameCode.length === 6 && !isLoadingAiGame
                          ? 'pointer'
                          : 'not-allowed',
                      fontWeight: 'bold',
                      opacity:
                        aiGameCode.length === 6 && !isLoadingAiGame ? 1 : 0.5,
                    }}
                  >
                    {isLoadingAiGame ? '⏳ Loading...' : '🤖 Join AI Game'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAiGameCodeInput(false);
                      setAiGameError(null);
                      setAiGameCode('');
                    }}
                    style={{
                      padding: '0.8rem 2rem',
                      fontSize: '1.1rem',
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          <div
            style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            {!showGameCodeInput && !showAiGameCodeInput && (
              <button
                onClick={handleEnterGameCode}
                style={{
                  padding: '1.2rem 2.5rem',
                  fontSize: '1.3rem',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 16px rgba(76,175,80,0.4)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow =
                    '0 6px 20px rgba(76,175,80,0.6)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow =
                    '0 4px 16px rgba(76,175,80,0.4)';
                }}
              >
                🔢 Enter Game Code
              </button>
            )}

            {!showGameCodeInput && !showAiGameCodeInput && (
              <button
                onClick={handlePlayWithAI}
                style={{
                  padding: '1.2rem 2.5rem',
                  fontSize: '1.3rem',
                  backgroundColor: '#FF9800',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 16px rgba(255,152,0,0.4)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow =
                    '0 6px 20px rgba(255,152,0,0.6)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow =
                    '0 4px 16px rgba(255,152,0,0.4)';
                }}
              >
                🤖 Play with AI
              </button>
            )}
          </div>
        </div>

        {!showGameCodeInput && !showAiGameCodeInput && (
          <button
            onClick={handleDemoGame}
            style={{
              padding: '0.9rem 2rem',
              fontSize: '1rem',
              backgroundColor: 'rgba(255,255,255,0.16)',
              color: 'white',
              border: '2px solid rgba(255,255,255,0.35)',
              borderRadius: '999px',
              cursor: 'pointer',
              fontWeight: 'bold',
              boxShadow: '0 6px 18px rgba(0,0,0,0.2)',
              backdropFilter: 'blur(10px)',
              transition:
                'transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 10px 22px rgba(0,0,0,0.28)';
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.22)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 6px 18px rgba(0,0,0,0.2)';
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.16)';
            }}
          >
            Demo Game
          </button>
        )}
      </div>

      {/* Background Animation */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: -1,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          overflow: 'hidden',
        }}
      >
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              width: '200px',
              height: '200px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.1)',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float ${3 + Math.random() * 4}s ease-in-out infinite ${Math.random() * 2}s`,
            }}
          />
        ))}
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0.7; }
          50% { transform: translateY(-20px) rotate(180deg); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default Lobby;
